import React, { useEffect, useRef, useState } from "react";
import { getPaymentPacks } from "../api/paymentApi";
import { useI18n } from "../i18n";
import "./OutOfTokensModal.css";

const BESTSELLER_PATTERN = /best\s*seller/i;
const price = (p) => `${(Number(p.priceAmountMinor || 0) / 100).toFixed(2)} ${p.currency || "PLN"}`;

function pickDefaultIndex(packs) {
  const byBadge = packs.findIndex((p) => BESTSELLER_PATTERN.test(p.badge || ""));
  return byBadge >= 0 ? byBadge : Math.floor((packs.length - 1) / 2);
}

function OutOfTokensModal({ isOpen, onClose, onBuy }) {
  const { t } = useI18n();
  const [packs, setPacks] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    let mounted = true;
    getPaymentPacks()
      .then(({ data }) => {
        if (!mounted) return;
        const chatPacks = (Array.isArray(data) ? data : []).filter((p) => p.kind === "CHAT_UNITS");
        setPacks(chatPacks);
        setActiveIndex(pickDefaultIndex(chatPacks));
      })
      .catch(() => mounted && setPacks([]));
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    const track = trackRef.current;
    const card = track && track.children[activeIndex];
    if (card) {
      track.scrollTo({ left: card.offsetLeft - (track.clientWidth - card.clientWidth) / 2, behavior: "auto" });
    }
  }, [packs, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleScroll() {
    const track = trackRef.current;
    if (!track || !track.children.length) return;
    const center = track.scrollLeft + track.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;
    Array.from(track.children).forEach((card, i) => {
      const distance = Math.abs(card.offsetLeft + card.clientWidth / 2 - center);
      if (distance < bestDistance) {
        best = i;
        bestDistance = distance;
      }
    });
    setActiveIndex(best);
  }

  function goTo(index) {
    setActiveIndex(index);
    const track = trackRef.current;
    const card = track && track.children[index];
    if (card) {
      track.scrollTo({ left: card.offsetLeft - (track.clientWidth - card.clientWidth) / 2, behavior: "smooth" });
    }
  }

  if (!isOpen) return null;
  const selected = packs[activeIndex];

  return (
    <div className="oot-overlay" onClick={onClose}>
      <div className="oot-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="oot-close" onClick={onClose} aria-label={t("outOfTokens.close")}>×</button>
        <h2 className="oot-title">{t("outOfTokens.title")}</h2>
        <p className="oot-text">{t("outOfTokens.text")}</p>

        {packs.length > 0 && (
          <>
            <div className="oot-carousel">
              <button type="button" className="oot-arrow" onClick={() => goTo(Math.max(0, activeIndex - 1))} disabled={activeIndex === 0} aria-label="‹">‹</button>
              <div className="oot-track" ref={trackRef} onScroll={handleScroll}>
                {packs.map((p, i) => (
                  <button
                    type="button"
                    key={p.packCode}
                    className={`oot-card ${i === activeIndex ? "active" : ""}`}
                    onClick={() => goTo(i)}
                  >
                    {p.badge && <span className="oot-badge">{p.badge}</span>}
                    <strong className="oot-card-title">{p.title || p.packCode}</strong>
                    <span className="oot-units">{p.unitsTotal} {t("outOfTokens.tokens")}</span>
                    {Number(p.unitsBonus) > 0 && <small className="oot-bonus">+{p.unitsBonus} {t("outOfTokens.free")}</small>}
                    <b className="oot-price">{price(p)}</b>
                  </button>
                ))}
              </div>
              <button type="button" className="oot-arrow" onClick={() => goTo(Math.min(packs.length - 1, activeIndex + 1))} disabled={activeIndex === packs.length - 1} aria-label="›">›</button>
            </div>
            <div className="oot-dots">
              {packs.map((p, i) => <span key={p.packCode} className={i === activeIndex ? "active" : ""} />)}
            </div>
          </>
        )}

        <button type="button" className="oot-buy" disabled={!selected} onClick={() => selected && onBuy(selected.packCode)}>
          {selected ? `${t("outOfTokens.buy")} · ${price(selected)}` : t("outOfTokens.buyGeneric")}
        </button>
      </div>
    </div>
  );
}

export default OutOfTokensModal;
